/**
 * Camera viewport controller — handles drag-pan (mouse middle/right button
 * and single-finger touch drag), scroll-zoom (mouse wheel and pinch), and
 * keyboard panning.
 */

import { Application, Container } from 'pixi.js';
import { InputMouseKeyboardHandler } from './input_mouse_keyboard_handler';

const MIN_SCALE = 0.25;
const MAX_SCALE = 2.0;
const ZOOM_SPEED = 0.001;

export class CameraViewportController {
  private app: Application;
  private input: InputMouseKeyboardHandler;
  private isMobile: boolean;
  private isDragging = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private worldX = 0;
  private worldY = 0;
  private scale = 1.0;

  constructor(app: Application, input: InputMouseKeyboardHandler, isMobile: boolean = false) {
    this.app = app;
    this.input = input;
    this.isMobile = isMobile;
  }

  getTransform(): { x: number; y: number; scale: number } {
    return { x: this.worldX, y: this.worldY, scale: this.scale };
  }

  /** Convert screen (canvas) coordinates to world coordinates. */
  screenToWorld(screenX: number, screenY: number): { x: number; y: number } {
    return {
      x: screenX / this.scale - this.worldX,
      y: screenY / this.scale - this.worldY,
    };
  }

  /** Convert world coordinates to screen (canvas) coordinates. */
  worldToScreen(worldX: number, worldY: number): { x: number; y: number } {
    return {
      x: (worldX + this.worldX) * this.scale,
      y: (worldY + this.worldY) * this.scale,
    };
  }

  update(): void {
    const st = this.input.state;
    const { mouseX, mouseY, mouseDown, mouseButton, mouseDeltaX, mouseDeltaY } = st;

    // Desktop: middle-button or right-button drag to pan
    if (!this.isMobile && mouseDown && (mouseButton === 1 || mouseButton === 2)) {
      if (!this.isDragging) {
        this.isDragging = true;
        this.dragStartX = mouseX - this.worldX;
        this.dragStartY = mouseY - this.worldY;
      }
      this.worldX = mouseX - this.dragStartX;
      this.worldY = mouseY - this.dragStartY;
    } else if (!this.isMobile) {
      this.isDragging = false;
    }

    // Mobile: single-finger drag pans the camera
    if (this.isMobile && st.touchDragging) {
      this.worldX += mouseDeltaX / this.scale;
      this.worldY += mouseDeltaY / this.scale;
    }

    // Arrow keys / WASD pan (desktop only — mobile has no keyboard)
    if (!this.isMobile) {
      const panSpeed = 5 / this.scale;
      const keys = st.keys;
      if (keys.has('ArrowLeft') || keys.has('a')) this.worldX += panSpeed;
      if (keys.has('ArrowRight') || keys.has('d')) this.worldX -= panSpeed;
      if (keys.has('ArrowUp') || keys.has('w')) this.worldY += panSpeed;
      if (keys.has('ArrowDown') || keys.has('s')) this.worldY -= panSpeed;
    }

    // Scroll wheel zoom (desktop) and pinch zoom (mobile) — both use wheelDelta
    const wheelDelta = st.wheelDelta;
    if (wheelDelta !== 0) {
      const prevScale = this.scale;
      this.scale *= (1 - wheelDelta * ZOOM_SPEED);
      this.scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, this.scale));
      // Zoom toward mouse/touch position
      const scaleChange = this.scale / prevScale;
      this.worldX = mouseX / this.scale - (mouseX / prevScale - this.worldX) / scaleChange;
      this.worldY = mouseY / this.scale - (mouseY / prevScale - this.worldY) / scaleChange;
    }
  }

  /** Transform a stage Container to reflect current camera position. */
  applyTo(stage: Container): void {
    stage.position.set(this.worldX * this.scale, this.worldY * this.scale);
    stage.scale.set(this.scale);
  }
}
